'use client'

import { Container, Flex } from '@quark/layout'
import React, { useState } from 'react'
import { Text } from '@quark/typography'
import { Layer } from '@quark/layer'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { Animations, MultiReveal } from '@quark/animation'
import { DriveHeroNewProps } from '@interfaces/Drivetypes'
import CarouselArrowButton from './CarouselArrowButton'
import { createSlides, getSlideClassName } from './utils'
import { useDriveHeroNewHandlers } from './useDriveHeroNewHandlers'
import styles from './DriveHeroNew.module.scss'

const DriveHeroNew = (data: DriveHeroNewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const rawProducts = data?.driveProducts || []
  const slides = createSlides(rawProducts)
  const activeSlide = slides[currentIndex]
  const ctaUrl = data?.buttonUrl || 'https://ackodrive.com/cars'

  const { handleCardClick, handlePrevious, handleNext, handleTouchStart, handleTouchMove, handleTouchEnd } =
    useDriveHeroNewHandlers({
      slidesLength: slides.length,
      activeCarName: activeSlide?.product?.carName,
      isAnimating,
      setIsAnimating,
      setCurrentIndex,
    })

  return (
    <Container id={NewHomePageComponent.DriveHeroNew} type="fluid">
      <div className={styles.driveHeroWrapper}>
        <Layer
          type="image"
          imgLg="https://acko-cms.ackoassets.com/Image_N3_1_09839a7468.webp"
          imgMd="https://acko-cms.ackoassets.com/Image_N3_tablet_9dfb8f5630.webp"
          imgSm="https://acko-cms.ackoassets.com/Image_N3_mobile_61d1a001cc.webp">
          <Container
            type="root"
            px={{ lg: 'spacing-80', md: 'spacing-32', sm: 'spacing-16' }}
            py={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-32' }}>
            <Flex
              flexDirection="column"
              gap={{ lg: 'spacing-40', md: 'spacing-32', sm: 'spacing-24' }}
              alignItems="center">
              <div className={styles.headerContainer}>
                <MultiReveal
                  as={Flex}
                  key="driveSection-headings"
                  flexDirection="column"
                  alignItems="center"
                  gap={{ lg: 'spacing-16', md: 'spacing-16', sm: 'spacing-8' }}
                  animationDelays={(i: number) => i * 250}
                  animationProps={{ animation: Animations.FadeIn }}>
                  {data?.driveHeading && (
                    <div className={styles.richTextHeader} dangerouslySetInnerHTML={{ __html: data.driveHeading }} />
                  )}
                </MultiReveal>
              </div>

              <div
                className={styles.carouselStage}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}>
                <div className={styles.platformBase}>
                  <img src="https://acko-cms.ackoassets.com/Ellipse_1_e243297f2d.webp" alt="Platform" />
                </div>

                <div className={styles.carsContainer}>
                  {slides.map((slide, index) => (
                    <div
                      key={slide.id}
                      className={`${styles.carSlide} ${getSlideClassName(index, currentIndex, slides.length, styles)}`}>
                      {slide.image?.url ? (
                        <img
                          src={slide.image.url}
                          alt={slide.image.alt}
                          className={styles.carImage}
                          width={600}
                          height={400}
                          loading="eager"
                        />
                      ) : (
                        <div className={styles.placeholderCar} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {activeSlide?.product && (
                <div className={styles.productCardWrapper}>
                  <CarouselArrowButton
                    className={styles.desktopArrow}
                    iconClassName={styles.arrowIcon}
                    direction="left"
                    onClick={handlePrevious}
                    ariaLabel="Previous"
                    size="desktop"
                  />

                  <div
                    className={styles.activeProductCard}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}>
                    <CarouselArrowButton
                      className={styles.mobileArrowLeft}
                      iconClassName={styles.mobileArrowIcon}
                      direction="left"
                      onClick={handlePrevious}
                      ariaLabel="Previous"
                      size="mobile"
                    />

                    <div className={styles.cardMiddleContent}>
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        size="fullWidth"
                        flexDirection={{ lg: 'row', md: 'column', sm: 'column' }}
                        gap={{ lg: 'spacing-0', md: 'spacing-8', sm: 'spacing-8' }}>
                        <div className={styles.cardLeft}>
                          <Flex
                            flexDirection="column"
                            alignItems={{ lg: 'flex-start', md: 'center', sm: 'center' }}
                            gap="spacing-4">
                            {activeSlide.product.carName && (
                              <h3 className={styles.carTitle}>{activeSlide.product.carName}</h3>
                            )}
                            <p className={styles.priceLabel}>Price starts from</p>
                          </Flex>
                        </div>

                        <div className={styles.cardRight}>
                          <Flex flexDirection="column" alignItems="center" gap="spacing-4">
                            <div className={styles.priceRow}>
                              <Flex
                                alignItems="baseline"
                                justifyContent={{ lg: 'flex-start', md: 'center', sm: 'center' }}
                                gap="spacing-8">
                                {activeSlide.product.carPrice && (
                                  <span className={styles.carPrice}>{activeSlide.product.carPrice}</span>
                                )}
                                {activeSlide.product.carOldPrice && (
                                  <span
                                    className={styles.carOldPrice}
                                    dangerouslySetInnerHTML={{ __html: activeSlide.product.carOldPrice }}
                                  />
                                )}
                              </Flex>
                            </div>
                            {activeSlide.product.carSave && (
                              <div className={styles.savePill}>{activeSlide.product.carSave}</div>
                            )}
                          </Flex>
                        </div>
                      </Flex>
                    </div>

                    <CarouselArrowButton
                      className={styles.mobileArrowRight}
                      iconClassName={styles.mobileArrowIcon}
                      direction="right"
                      onClick={handleNext}
                      ariaLabel="Next"
                      size="mobile"
                    />
                  </div>
                  <CarouselArrowButton
                    className={styles.desktopArrow}
                    iconClassName={styles.arrowIcon}
                    direction="right"
                    onClick={handleNext}
                    ariaLabel="Next"
                    size="desktop"
                  />
                </div>
              )}

              <div className={styles.ctaSection}>
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  gap={{ lg: 'spacing-16', md: 'spacing-12', sm: 'spacing-8' }}>
                  <button
                    type="button"
                    className={styles.ctaButton}
                    onClick={e => {
                      e.preventDefault()
                      void handleCardClick(
                        {
                          customProperties: {
                            journey: 'acko_drive_car_visit_showroom',
                            vehicle_make_model: activeSlide?.product?.carName,
                          },
                        },
                        ctaUrl,
                        false
                      )
                    }}>
                    <Text
                      fontStyle={{ lg: 'um_text-lg/medium', md: 'um_text-base/medium', sm: 'um_text-sm/medium' }}
                      color="base/inverted primary"
                      textAlign="center">
                      {data?.buttonText || 'Visit our digital showroom'}
                    </Text>
                  </button>
                  <Flex
                    gap={{ lg: 'spacing-8', md: 'spacing-8', sm: 'spacing-4' }}
                    justifyContent="center"
                    alignItems="center">
                    <Text
                      fontStyle={{ lg: 'um_text-sm/normal', md: 'um_text-sm/normal', sm: 'um_text-xs/normal' }}
                      color="base/primary">
                      Powered by
                    </Text>
                    <picture className={styles.adLogo}>
                      <img
                        src="https://acko-cms.ackoassets.com/ACKO_Drive_wide_0f43e08c1c.svg"
                        alt="ACKO DRIVE"
                        width={95}
                        height={18}
                      />
                    </picture>
                  </Flex>
                </Flex>
              </div>
            </Flex>
          </Container>
        </Layer>
      </div>
    </Container>
  )
}

export default DriveHeroNew
